import { config } from '_appConfig'

import { apiClient } from './api-client'
import { CONTENT_SPACE_ROUTES } from './constants'
import type {
  ContentSpacePublishStatus,
  IContentSpaceIntegration,
  ICourseGroup,
  ICoursePortal,
  ICourseVersion,
  IGetCoursesParams,
  IGetPortalsParams,
  IListResponse,
  ILmsCourse,
  ILmsCourseInput,
  IScormUserInfo,
  IContentSpaceSession,
  ContentSpaceSessionType,
  ContentSpaceSessionStatus,
  IContentSpaceGroupPermissions,
  IScormEvent,
  IXapiStatement,
  IPortalSsoConfig,
  IPortalSsoConfigInput,
  IPortalSsoConfigUpdate,
  IPublicSsoConfig,
  ICustomDomainStatus,
  ICustomDomainVerifyResult,
  SessionResumeMode,
  IContentSpaceWebhook,
  IContentSpaceWebhookInput,
  IExportCountResponse,
  IPortalRedirect,
  IPortalRedirectInput,
} from './types'

export const URL = config.io.contentspaceEndpoint

/**
 * Initialize ContentSpace for a company
 */
export const initializeContentSpace = (companyId: string) => {
  return apiClient.post(CONTENT_SPACE_ROUTES.PRIVATE.INITIALIZE(companyId), { companyId })
}

/**
 * Create a new course
 */
export const createCourse = (course: ILmsCourseInput, companyId: string) => {
  return apiClient.post<ILmsCourse>(CONTENT_SPACE_ROUTES.PRIVATE.COURSES(companyId), {
    ...course,
    companyId,
  })
}

/**
 * Upload a file for a course
 * @deprecated Use uploadCourse instead
 */
export const uploadFile = (file: File, courseId: string, companyId: string) => {
  const formData = new FormData()
  formData.append('file', file)
  return apiClient.upload<string>(
    CONTENT_SPACE_ROUTES.PRIVATE.COURSE_UPLOAD(companyId, courseId),
    formData,
  )
}

/**
 * Get the URL for accessing a course
 */
export const getCourseUrl = (course: ILmsCourse) => {
  if (course.url) {
    return course.url
  }
  return `${URL}${CONTENT_SPACE_ROUTES.STATIC.COURSE(course.id, course.indexFile || 'index.html')}`
}

/**
 * Save course SCORM data
 */
export const saveCourseData = (
  courseId: string,
  data: unknown,
  companyId: string,
  userInfo?: IScormUserInfo | null,
) => {
  return apiClient.put<string>(CONTENT_SPACE_ROUTES.PRIVATE.COURSE_DATA(companyId, courseId), {
    data,
    actor: userInfo
      ? {
          id: userInfo.id,
          email: userInfo.email,
          name: userInfo.name,
        }
      : undefined,
  })
}

/**
 * Reset course SCORM data to empty object
 */
export const resetCourseData = (courseId: string, companyId: string) => {
  return apiClient.put<string>(CONTENT_SPACE_ROUTES.PRIVATE.COURSE_DATA(companyId, courseId), {
    data: {},
  })
}

/**
 * Reset the entire LMS storage
 */
export const resetLms = (companyId: string) => {
  return apiClient.post<string>(CONTENT_SPACE_ROUTES.PRIVATE.RESET(companyId))
}

/**
 * Update course details
 */
export const updateCourse = (courseId: string, course: Partial<ILmsCourse>, companyId: string) => {
  return apiClient.put<ILmsCourse>(CONTENT_SPACE_ROUTES.PRIVATE.COURSE(companyId, courseId), {
    ...course,
    companyId,
  })
}

// Build a sorted, undefined-free query-string for stable cache-key normalization (ADR-0001 §5.3)
const buildListQuery = (params: Record<string, unknown> | undefined): string => {
  if (!params) {
    return ''
  }
  const search = new URLSearchParams()
  Object.keys(params)
    .sort()
    .forEach((key) => {
      const value = params[key]
      if (value === undefined || value === null) {
        return
      }
      if (Array.isArray(value)) {
        if (value.length === 0) {
          return
        }
        search.set(key, value.join(','))
        return
      }
      search.set(key, String(value))
    })
  const str = search.toString()
  return str ? `?${str}` : ''
}

/**
 * Get paginated courses for a company (supports server-side filters/sort)
 */
export const getCourses = (
  companyId: string,
  params?: IGetCoursesParams,
): Promise<IListResponse<ILmsCourse>> => {
  const qs = buildListQuery(params as Record<string, unknown> | undefined)
  return apiClient.get<IListResponse<ILmsCourse>>(
    `${CONTENT_SPACE_ROUTES.PRIVATE.COURSES(companyId)}${qs}`,
  )
}

/**
 * Get paginated portals for a company
 */
export const getPortals = (
  companyId: string,
  params?: IGetPortalsParams,
): Promise<IListResponse<ICoursePortal>> => {
  const qs = buildListQuery(params as Record<string, unknown> | undefined)
  return apiClient.get<IListResponse<ICoursePortal>>(
    `${CONTENT_SPACE_ROUTES.PRIVATE.PORTALS(companyId)}${qs}`,
  )
}

/**
 * Fetch all portals for a company by paginating through the list endpoint.
 * Handles datasets larger than the backend's MAX_PAGE_SIZE cap.
 */
export const getAllPortals = async (
  companyId: string,
  params?: Omit<IGetPortalsParams, 'page' | 'pageSize'>,
): Promise<ICoursePortal[]> => {
  const PAGE_SIZE = 200
  const collected: ICoursePortal[] = []
  let page = 1
  while (true) {
    const response = await getPortals(companyId, { ...(params ?? {}), page, pageSize: PAGE_SIZE })
    collected.push(...response.data)
    if (collected.length >= (response.total ?? 0) || response.data.length < PAGE_SIZE) {
      break
    }
    page += 1
  }
  return collected
}

/**
 * Get a single course by ID
 */
export const getCourse = (courseId: string, companyId: string): Promise<ILmsCourse> => {
  return apiClient.get<ILmsCourse>(CONTENT_SPACE_ROUTES.PRIVATE.COURSE(companyId, courseId))
}

/**
 * Upload a SCORM package for a course
 */
export const uploadCourse = (courseId: string, file: File, companyId: string) => {
  const formData = new FormData()
  formData.append('file', file)
  return apiClient.upload(CONTENT_SPACE_ROUTES.PRIVATE.COURSE_UPLOAD(companyId, courseId), formData)
}

/**
 * Get all course groups for a company
 */
export const getCourseGroups = (companyId: string) => {
  return apiClient.get<ICourseGroup[]>(CONTENT_SPACE_ROUTES.PRIVATE.GROUPS(companyId))
}

/**
 * Create a new course group
 */
export const createCourseGroup = (
  name: string,
  description: string | undefined,
  companyId: string,
) => {
  return apiClient.post<ICourseGroup>(CONTENT_SPACE_ROUTES.PRIVATE.GROUPS(companyId), {
    name,
    description,
    companyId,
  })
}

/**
 * Update an existing course group
 */
export const updateCourseGroup = (
  groupId: string,
  name: string,
  description: string | undefined,
  companyId: string,
  permissions?: IContentSpaceGroupPermissions & { startCourseId?: string | null },
) => {
  return apiClient.put<ICourseGroup>(CONTENT_SPACE_ROUTES.PRIVATE.GROUP(companyId, groupId), {
    name,
    description,
    companyId,
    ...permissions,
  })
}

/**
 * Move a course to a different group
 */
export const moveCourseToGroup = (courseId: string, groupId: string | null, companyId: string) => {
  return apiClient.put<ILmsCourse>(CONTENT_SPACE_ROUTES.PRIVATE.COURSE(companyId, courseId), {
    courseGroupId: groupId,
  })
}

/**
 * Get all public courses (accessible without authentication)
 */
export const getPublicCourses = (companyId: string): Promise<ILmsCourse[]> => {
  return apiClient.get<ILmsCourse[]>(CONTENT_SPACE_ROUTES.PUBLIC.COURSES(companyId))
}

/**
 * Generate a shareable link for a course
 */
export const getCourseShareableLink = (
  companyId: string,
  groupId: string,
  courseId: string,
  baseUrl: string = window.location.origin,
) => {
  return `${baseUrl}/content-space/${companyId}/group/${groupId}/course/${courseId}`
}

/**
 * Generate a shareable link for a group (public landing page)
 */
export const getGroupShareableLink = (
  companyId: string,
  groupId: string,
  baseUrl: string = window.location.origin,
) => {
  return `${baseUrl}/content-space/${companyId}/group/${groupId}`
}

export const getPortalPublicUrl = (
  portal: { id: string; customDomain?: string | null; customDomainStatus?: string | null },
  _companyId: string,
  courseId?: string,
  deepLink?: { sectionId?: string | null; blockId?: string | null },
): string => {
  const sectionSuffix = deepLink?.sectionId ? `/sections/${deepLink.sectionId}` : ''
  // Inner-course block scroll uses `#block-{id}` — mirrors BE
  // `buildPortalCanonicalUrl` and FE router expectations.
  const blockHash = deepLink?.blockId ? `#block-${deepLink.blockId}` : ''

  const hasCustomDomain = portal.customDomain && portal.customDomainStatus === 'ACTIVE'
  if (hasCustomDomain) {
    const base = `https://${portal.customDomain}`
    if (!courseId) {
      return base
    }
    return `${base}/courses/${courseId}${sectionSuffix}${blockHash}`
  }
  const portalBase = `${window.location.origin}/contentspace/public/portal/${portal.id}`
  if (!courseId) {
    return portalBase
  }
  return `${portalBase}/courses/${courseId}${sectionSuffix}${blockHash}`
}

// Custom-domain ACTIVE → `https://{customDomain}/logout`; иначе SSR-урл
// `/contentspace/public/portal/{portalId}/logout`, который монтирует
// PortalV2SpaRouter на бэке.
export const getPortalLogoutUrl = (portal: {
  id: string
  customDomain?: string | null
  customDomainStatus?: string | null
}): string => {
  const hasCustomDomain = portal.customDomain && portal.customDomainStatus === 'ACTIVE'
  if (hasCustomDomain) {
    return `https://${portal.customDomain}/logout`
  }
  return `${window.location.origin}/contentspace/public/portal/${portal.id}/logout`
}

export const createSession = (
  companyId: string,
  groupId: string,
  courseId: string,
  data: { type: ContentSpaceSessionType; email?: string; userName?: string; isDebug?: boolean },
) =>
  apiClient.post<{ session: IContentSpaceSession; sessionToken: string }>(
    `/public/companies/${companyId}/groups/${groupId}/courses/${courseId}/sessions`,
    data,
  )

// Backend accepts either session.sessionToken as the path segment, or session.id +
// sessionToken query param. Passing both keeps legacy callers (sessionId in cookie)
// working while enforcing proof-of-ownership on id-based lookups.
export const getSessionById = (sessionId: string, sessionToken?: string) => {
  const query = sessionToken ? `?sessionToken=${encodeURIComponent(sessionToken)}` : ''
  return apiClient.get<{ session: IContentSpaceSession }>(`/public/sessions/${sessionId}${query}`)
}

export const updateSession = (
  sessionId: string,
  data: {
    sessionToken?: string
    progress?: number
    score?: number
    status?: ContentSpaceSessionStatus
    location?: string
    cmiData?: unknown
  },
) => apiClient.put<{ session: IContentSpaceSession }>(`/public/sessions/${sessionId}`, data)

export const sendVerificationCode = (companyId: string, email: string, groupId: string) =>
  apiClient.post<{ success: boolean; expiresIn: number }>(
    `/public/companies/${companyId}/verify/send`,
    {
      email,
      groupId,
    },
  )

export const verifyEmailCode = (companyId: string, email: string, groupId: string, code: string) =>
  apiClient.post<{ success: boolean; email: string; verified: boolean }>(
    `/public/companies/${companyId}/verify/confirm`,
    { email, groupId, code },
  )

export interface IGetCourseSessionsParams {
  type?: ContentSpaceSessionType
  status?: ContentSpaceSessionStatus
  search?: string
  limit?: number
  offset?: number
}

export const getCourseSessions = (
  companyId: string,
  courseId: string,
  params: IGetCourseSessionsParams = {},
) => {
  const { type, status, search, limit, offset } = params
  const query = new URLSearchParams()
  if (type) {
    query.set('type', type)
  }
  if (status) {
    query.set('status', status)
  }
  if (search) {
    query.set('search', search)
  }
  if (limit !== undefined) {
    query.set('limit', String(limit))
  }
  if (offset !== undefined) {
    query.set('offset', String(offset))
  }
  const suffix = query.toString() ? `?${query.toString()}` : ''
  return apiClient.get<{ sessions: IContentSpaceSession[]; total: number }>(
    `/private/companies/${companyId}/courses/${courseId}/sessions${suffix}`,
  )
}

export interface ICourseSessionsAnalytics {
  total: number
  uniqueLearners: number
  completedCount: number
  passedCount: number
  averageScore: number | null
  byType: Record<ContentSpaceSessionType, number>
  byStatus: Record<ContentSpaceSessionStatus, number>
}

export const getCourseSessionsAnalytics = (companyId: string, courseId: string) =>
  apiClient.get<ICourseSessionsAnalytics>(
    `/private/companies/${companyId}/courses/${courseId}/sessions/analytics`,
  )

export const createInternalSession = (
  companyId: string,
  courseId: string,
  data: { kuserId: string; email?: string; userName?: string; isDebug?: boolean },
) =>
  apiClient.post<{ session: IContentSpaceSession }>(
    `/private/companies/${companyId}/courses/${courseId}/sessions`,
    data,
  )

export const getSessionXapiLogs = (companyId: string, sessionId: string) =>
  apiClient.get<{ statements: IXapiStatement[] }>(
    `/private/companies/${companyId}/sessions/${sessionId}/xapi-logs`,
  )

export const getSessionScormLogs = (companyId: string, sessionId: string) =>
  apiClient.get<{ events: IScormEvent[]; total: number }>(
    `/private/companies/${companyId}/sessions/${sessionId}/scorm-logs`,
  )

export const saveScormEvents = (
  sessionId: string,
  sessionToken: string,
  events: Omit<IScormEvent, 'id' | 'sessionId'>[],
) =>
  apiClient.post<{ count: number }>(`/public/sessions/${sessionId}/scorm-events`, {
    sessionToken,
    events,
  })

export const getPublicCourse = (companyId: string, groupId: string, courseId: string) =>
  apiClient.get<ILmsCourse & { courseGroup: { permissions: IContentSpaceGroupPermissions } }>(
    `/public/companies/${companyId}/groups/${groupId}/courses/${courseId}`,
  )

export interface IPublicGroupData {
  id: string
  name: string
  description?: string
  permissions: IContentSpaceGroupPermissions
  courses: Pick<ILmsCourse, 'id' | 'name' | 'description' | 'url' | 'status'>[]
  startCourseId?: string
}

export const getPublicGroup = (companyId: string, groupId: string) =>
  apiClient.get<IPublicGroupData>(`/public/companies/${companyId}/groups/${groupId}`)

export const getLrsConfig = (sessionId?: string) =>
  apiClient.get<{ enabled: boolean; endpoint?: string; auth?: string }>(
    `/public/xapi/lrs-config${sessionId ? `?sessionId=${sessionId}` : ''}`,
  )

export const deleteSession = (companyId: string, sessionId: string) =>
  apiClient.delete(`/private/companies/${companyId}/sessions/${sessionId}`)

/**
 * Delete a course
 */
export const deleteCourse = (courseId: string, companyId: string) => {
  return apiClient.delete<void>(CONTENT_SPACE_ROUTES.PRIVATE.COURSE(companyId, courseId))
}

/**
 * Delete a course group
 */
export const deleteCourseGroup = (companyId: string, groupId: string) => {
  return apiClient.delete(CONTENT_SPACE_ROUTES.PRIVATE.GROUP(companyId, groupId))
}

// ========== PORTAL API (N:M) ==========

/**
 * Get all course portals for a company
 */
export const getCoursePortals = (companyId: string) => {
  return apiClient.get<ICoursePortal[]>(CONTENT_SPACE_ROUTES.PRIVATE.PORTALS(companyId))
}

/**
 * Create a new course portal
 */
export const createCoursePortal = (
  name: string,
  description: string | undefined,
  companyId: string,
) => {
  return apiClient.post<ICoursePortal>(CONTENT_SPACE_ROUTES.PRIVATE.PORTALS(companyId), {
    name,
    description,
    companyId,
  })
}

/**
 * Update an existing course portal
 */
export const updateCoursePortal = (
  portalId: string,
  name: string,
  description: string | undefined,
  companyId: string,
  permissions?: IContentSpaceGroupPermissions & {
    startCourseId?: string | null
    hideHeader?: boolean
    sessionResumeMode?: SessionResumeMode
    brandId?: string | null
    certificateTemplateId?: string | null
  },
  signal?: AbortSignal,
) => {
  return apiClient.put<ICoursePortal>(
    CONTENT_SPACE_ROUTES.PRIVATE.PORTAL(companyId, portalId),
    {
      name,
      description,
      companyId,
      ...permissions,
    },
    signal,
  )
}

/**
 * Update portal publish status (DRAFT / PUBLISHED)
 */
export const updatePortalStatus = (
  companyId: string,
  portalId: string,
  status: ContentSpacePublishStatus,
) => {
  return apiClient.patch<ICoursePortal>(
    CONTENT_SPACE_ROUTES.PRIVATE.PORTAL_STATUS(companyId, portalId),
    { status },
  )
}

/**
 * Update course publish status (DRAFT / PUBLISHED).
 *
 * Invariant: a course attached to any portal must be PUBLISHED. Setting DRAFT
 * on such a course requires `force: true`, which detaches it from all portals
 * atomically. Without `force` the backend returns 409 + `{ portalIds }`.
 */
export const updateCourseStatus = (
  companyId: string,
  courseId: string,
  status: ContentSpacePublishStatus,
  options: { force?: boolean } = {},
) => {
  return apiClient.patch<ILmsCourse>(
    CONTENT_SPACE_ROUTES.PRIVATE.COURSE_STATUS(companyId, courseId),
    options.force ? { status, force: true } : { status },
  )
}

/**
 * Delete a course portal
 */
export const deleteCoursePortal = (companyId: string, portalId: string) => {
  return apiClient.delete(CONTENT_SPACE_ROUTES.PRIVATE.PORTAL(companyId, portalId))
}

/**
 * Set portals for a course (M:N relationship)
 */
export const setCoursePortals = (courseId: string, portalIds: string[], companyId: string) => {
  return apiClient.put<{ message: string }>(
    CONTENT_SPACE_ROUTES.PRIVATE.COURSE_PORTALS(companyId, courseId),
    { portalIds },
  )
}

/**
 * Bulk attach: link many courses to a single portal.
 * Backend skips already-linked pairs and returns counts.
 */
export const attachCoursesToPortal = (companyId: string, portalId: string, courseIds: string[]) => {
  return apiClient.post<{ added: number; skipped: number }>(
    CONTENT_SPACE_ROUTES.PRIVATE.PORTAL_COURSES_ATTACH(companyId, portalId),
    { courseIds },
  )
}

/**
 * Get portal IDs for a course
 */
export const getCoursePortalIds = (courseId: string, companyId: string) => {
  return apiClient.get<string[]>(CONTENT_SPACE_ROUTES.PRIVATE.COURSE_PORTALS(companyId, courseId))
}

/**
 * Get portals associated with a course (final signature per Task 1.4)
 */
export const getCoursePortalsForCourse = (companyId: string, courseId: string) => {
  return apiClient.get<string[]>(CONTENT_SPACE_ROUTES.PRIVATE.COURSE_PORTALS(companyId, courseId))
}

/**
 * Get ContentSpace integrations for a portal.
 * NOTE: backend endpoint is a gap (see team/backend-contracts.md §2, Task 1.3).
 * Returns empty array until the endpoint ships — call-sites can render "Coming soon".
 */
export const getIntegrations = async (
  _companyId: string,
  _portalId: string,
): Promise<IContentSpaceIntegration[]> => {
  return []
}

/**
 * Generate a shareable link for a course in a portal
 */
export const getCourseShareableLinkPortal = (
  companyId: string,
  portalId: string,
  courseId: string,
  baseUrl: string = window.location.origin,
) => {
  return `${baseUrl}/content-space/${companyId}/portal/${portalId}/course/${courseId}`
}

/**
 * Generate a shareable link for a portal (public landing page)
 */
export const getPortalShareableLink = (
  companyId: string,
  portalId: string,
  baseUrl: string = window.location.origin,
) => {
  return `${baseUrl}/content-space/${companyId}/portal/${portalId}`
}

export interface IPublicPortalData {
  id: string
  name: string
  description?: string
  permissions: IContentSpaceGroupPermissions
  courses: Pick<ILmsCourse, 'id' | 'name' | 'description' | 'url'>[]
  startCourseId?: string
  hideHeader?: boolean
  sessionResumeMode?: SessionResumeMode
  ssoConfigs?: IPublicSsoConfig[]
}

export const getPublicPortal = (companyId: string, portalId: string) =>
  apiClient.get<IPublicPortalData>(`/public/companies/${companyId}/groups/portal/${portalId}`)

export interface IPublicPortalCourseData extends ILmsCourse {
  coursePortal: {
    id: string
    name: string
    hideHeader?: boolean
    sessionResumeMode?: SessionResumeMode
    startCourseId?: string
    permissions: IContentSpaceGroupPermissions
  }
}

export const getPublicPortalCourse = (companyId: string, portalId: string, courseId: string) =>
  apiClient.get<IPublicPortalCourseData>(
    `/public/companies/${companyId}/groups/portal/${portalId}/courses/${courseId}`,
  )

export const createPortalSession = (
  companyId: string,
  portalId: string,
  courseId: string,
  data: {
    type: ContentSpaceSessionType
    email?: string
    userName?: string
    isDebug?: boolean
    // Force a fresh row + new course.enrollment.created webhook even if an active session
    // already exists for (portal, course, user). Used by "Start New Session".
    forceNew?: boolean
  },
) =>
  apiClient.post<{ session: IContentSpaceSession; sessionToken: string }>(
    `/public/companies/${companyId}/groups/portal/${portalId}/courses/${courseId}/sessions`,
    data,
  )

export const sendPortalVerificationCode = (companyId: string, email: string, portalId: string) =>
  apiClient.post<{ success: boolean; expiresIn: number }>(
    `/public/companies/${companyId}/verify/send`,
    {
      email,
      groupId: portalId, // API uses groupId for identifier
    },
  )

export const verifyPortalEmailCode = (
  companyId: string,
  email: string,
  portalId: string,
  code: string,
) =>
  apiClient.post<{ success: boolean; email: string; verified: boolean }>(
    `/public/companies/${companyId}/verify/confirm`,
    { email, groupId: portalId, code }, // API uses groupId for identifier
  )

/**
 * Check if a session is eligible for certificate download
 */
export const isSessionEligibleForCertificate = (status: ContentSpaceSessionStatus): boolean => {
  return status === 'PASSED' || status === 'COMPLETED'
}

const triggerPdfDownload = async (response: Response): Promise<void> => {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({
      error: 'Failed to download certificate',
    }))
    throw new Error(errorData.error || errorData.message || 'Download failed')
  }

  const blob = await response.blob()
  const contentDisposition = response.headers.get('Content-Disposition')
  let fileName = 'certificate.pdf'

  if (contentDisposition) {
    const match = contentDisposition.match(/filename="(.+)"/)
    if (match) {
      fileName = match[1]
    }
  }

  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  window.URL.revokeObjectURL(url)
}

/**
 * Download completion certificate for a session (admin-side, uses company auth)
 */
export const downloadCertificate = async (companyId: string, sessionId: string): Promise<void> => {
  const response = await fetch(
    `${URL}/private/companies/${companyId}/sessions/${sessionId}/certificate`,
    { credentials: 'include' },
  )
  await triggerPdfDownload(response)
}

/**
 * Download completion certificate for a learner session (public player)
 */
export const downloadPublicCertificate = async (
  sessionId: string,
  sessionToken: string,
): Promise<void> => {
  const response = await fetch(
    `${URL}/public/sessions/${sessionId}/certificate?sessionToken=${encodeURIComponent(sessionToken)}`,
  )
  await triggerPdfDownload(response)
}

// ========== PER-COURSE PORTAL API ==========

/**
 * Create or get a per-course portal (hidden portal for direct course access)
 */
export const createOrGetPerCoursePortal = (companyId: string, courseId: string) => {
  return apiClient.post<ICoursePortal>(
    CONTENT_SPACE_ROUTES.PRIVATE.PER_COURSE_PORTAL(companyId, courseId),
  )
}

/**
 * Get per-course portal for a course
 */
export const getPerCoursePortal = (companyId: string, courseId: string) => {
  return apiClient.get<ICoursePortal>(
    CONTENT_SPACE_ROUTES.PRIVATE.PER_COURSE_PORTAL(companyId, courseId),
  )
}

// ========== PORTAL SSO API ==========

export const getSsoConfigs = (companyId: string, portalId: string) =>
  apiClient.get<{ configs: IPortalSsoConfig[] }>(
    CONTENT_SPACE_ROUTES.PRIVATE.SSO_CONFIGS(companyId, portalId),
  )

export const createSsoConfig = (companyId: string, portalId: string, data: IPortalSsoConfigInput) =>
  apiClient.post<{ config: IPortalSsoConfig; apiKey?: string }>(
    CONTENT_SPACE_ROUTES.PRIVATE.SSO_CONFIGS(companyId, portalId),
    data,
  )

export const updateSsoConfig = (
  companyId: string,
  portalId: string,
  configId: string,
  data: IPortalSsoConfigUpdate,
) =>
  apiClient.put<{ config: IPortalSsoConfig }>(
    CONTENT_SPACE_ROUTES.PRIVATE.SSO_CONFIG(companyId, portalId, configId),
    data,
  )

export const deleteSsoConfig = (companyId: string, portalId: string, configId: string) =>
  apiClient.delete(CONTENT_SPACE_ROUTES.PRIVATE.SSO_CONFIG(companyId, portalId, configId))

export const testSsoConfig = (companyId: string, portalId: string, configId: string) =>
  apiClient.post<{ authUrl: string; redirectUri: string }>(
    CONTENT_SPACE_ROUTES.PRIVATE.SSO_CONFIG_TEST(companyId, portalId, configId),
  )

export const applySsoConfig = (companyId: string, portalId: string, configId: string) =>
  apiClient.post<{ config: IPortalSsoConfig }>(
    CONTENT_SPACE_ROUTES.PRIVATE.SSO_CONFIG_APPLY(companyId, portalId, configId),
  )

export const regenerateSsoApiKey = (companyId: string, portalId: string, configId: string) =>
  apiClient.post<{ apiKey: string }>(
    CONTENT_SPACE_ROUTES.PRIVATE.SSO_CONFIG_REGENERATE_API_KEY(companyId, portalId, configId),
  )

// ========== WEBHOOK API ==========

export const getWebhooks = (companyId: string, portalId: string) =>
  apiClient.get<{ webhooks: IContentSpaceWebhook[] }>(
    CONTENT_SPACE_ROUTES.PRIVATE.WEBHOOKS(companyId, portalId),
  )

export const createWebhook = (
  companyId: string,
  portalId: string,
  data: IContentSpaceWebhookInput,
) =>
  apiClient.post<{ webhook: IContentSpaceWebhook }>(
    CONTENT_SPACE_ROUTES.PRIVATE.WEBHOOKS(companyId, portalId),
    data,
  )

export const updateWebhook = (
  companyId: string,
  portalId: string,
  webhookId: string,
  data: Partial<IContentSpaceWebhookInput>,
) =>
  apiClient.put<{ webhook: IContentSpaceWebhook }>(
    CONTENT_SPACE_ROUTES.PRIVATE.WEBHOOK(companyId, portalId, webhookId),
    data,
  )

export const deleteWebhook = (companyId: string, portalId: string, webhookId: string) =>
  apiClient.delete(CONTENT_SPACE_ROUTES.PRIVATE.WEBHOOK(companyId, portalId, webhookId))

// ========== SSO SESSION API ==========

export const getSsoSessionStatus = (companyId: string, portalId: string) =>
  apiClient.get<{
    authenticated: boolean
    email?: string
    userName?: string
    endUserId?: string
  }>(`/public/companies/${companyId}/portals/${portalId}/sso/me`)

// ========== CUSTOM DOMAIN API ==========

export const getCustomDomainStatus = (companyId: string, portalId: string) =>
  apiClient.get<ICustomDomainStatus>(
    CONTENT_SPACE_ROUTES.PRIVATE.PORTAL_CUSTOM_DOMAIN(companyId, portalId),
  )

export const attachCustomDomain = (companyId: string, portalId: string, domain: string) =>
  apiClient.put<ICustomDomainStatus>(
    CONTENT_SPACE_ROUTES.PRIVATE.PORTAL_CUSTOM_DOMAIN(companyId, portalId),
    { domain },
  )

export const detachCustomDomain = (companyId: string, portalId: string) =>
  apiClient.delete<{ success: boolean }>(
    CONTENT_SPACE_ROUTES.PRIVATE.PORTAL_CUSTOM_DOMAIN(companyId, portalId),
  )

export const verifyCustomDomain = (companyId: string, portalId: string) =>
  apiClient.post<ICustomDomainVerifyResult>(
    CONTENT_SPACE_ROUTES.PRIVATE.PORTAL_CUSTOM_DOMAIN_VERIFY(companyId, portalId),
  )

export const reconcileCustomDomain = (companyId: string, portalId: string) =>
  apiClient.post<{ domain: string; ingress: string; action: string }>(
    CONTENT_SPACE_ROUTES.PRIVATE.PORTAL_CUSTOM_DOMAIN_RECONCILE(companyId, portalId),
  )

// ========== COURSE VERSIONS API ==========

export const getCourseVersions = (courseId: string, companyId: string) => {
  return apiClient.get<ICourseVersion[]>(
    CONTENT_SPACE_ROUTES.PRIVATE.COURSE_VERSIONS(companyId, courseId),
  )
}

export const activateCourseVersion = (courseId: string, versionId: string, companyId: string) => {
  return apiClient.post<ICourseVersion[]>(
    CONTENT_SPACE_ROUTES.PRIVATE.COURSE_VERSION_ACTIVATE(companyId, courseId, versionId),
    {},
  )
}

export const downloadCourseVersion = (courseId: string, versionId: string, companyId: string) => {
  window.location.href = `${URL}${CONTENT_SPACE_ROUTES.PRIVATE.COURSE_VERSION_DOWNLOAD(companyId, courseId, versionId)}`
}

// ========== SESSIONS EXPORT API ==========

interface IExportScope {
  companyId: string
  courseId?: string
  portalId?: string
}

interface IExportRange {
  from: Date
  to: Date
}

interface IExportOpts {
  includeEvents: boolean
}

const buildExportBasePath = (scope: IExportScope, suffix: string): string => {
  const { companyId, courseId, portalId } = scope
  if (courseId) {
    return `/private/companies/${companyId}/courses/${courseId}/sessions/${suffix}`
  }
  return `/private/companies/${companyId}/portals/${portalId}/sessions/${suffix}`
}

export const getSessionsExportCount = (
  scope: IExportScope,
  range: IExportRange,
  opts: IExportOpts,
): Promise<IExportCountResponse> => {
  const query = new URLSearchParams({
    from: range.from.toISOString(),
    to: range.to.toISOString(),
    includeEvents: String(opts.includeEvents),
  })
  return apiClient.get<IExportCountResponse>(
    `${buildExportBasePath(scope, 'export.count')}?${query.toString()}`,
  )
}

export interface ICertificateTemplateOption {
  id: string
  name: string
}

export const listPortalCertificateTemplates = (
  companyId: string,
  portalId: string,
): Promise<ICertificateTemplateOption[]> => {
  return apiClient.get<ICertificateTemplateOption[]>(
    `${CONTENT_SPACE_ROUTES.PRIVATE.PORTAL(companyId, portalId)}/certificate-templates`,
  )
}

export const getSessionsExportDownloadUrl = (
  scope: IExportScope,
  range: IExportRange,
  opts: IExportOpts,
): string => {
  const query = new URLSearchParams({
    from: range.from.toISOString(),
    to: range.to.toISOString(),
    includeEvents: String(opts.includeEvents),
    _ts: String(Date.now()),
  })
  return `${URL}${buildExportBasePath(scope, 'export.json')}?${query.toString()}`
}

// ========== PORTAL REDIRECTS API ==========

export const listPortalRedirects = (
  companyId: string,
  portalId: string,
): Promise<IPortalRedirect[]> =>
  apiClient
    .get<{
      redirects: IPortalRedirect[]
    }>(CONTENT_SPACE_ROUTES.PRIVATE.PORTAL_REDIRECTS(companyId, portalId))
    .then((json) => json.redirects)

export const createPortalRedirect = async (
  companyId: string,
  portalId: string,
  input: IPortalRedirectInput,
): Promise<IPortalRedirect> => {
  try {
    return await apiClient.post<IPortalRedirect>(
      CONTENT_SPACE_ROUTES.PRIVATE.PORTAL_REDIRECTS(companyId, portalId),
      input,
    )
  } catch (e: unknown) {
    const response = (e as { response?: Response }).response
    if (response) {
      const json = await response.json().catch(() => ({}))
      const err = new Error('createPortalRedirect failed') as Error & { errors?: unknown }
      err.errors = json.errors
      throw err
    }
    throw e
  }
}

export const updatePortalRedirect = async (
  companyId: string,
  portalId: string,
  redirectId: string,
  patch: Partial<IPortalRedirectInput>,
): Promise<IPortalRedirect> => {
  try {
    return await apiClient.patch<IPortalRedirect>(
      CONTENT_SPACE_ROUTES.PRIVATE.PORTAL_REDIRECT(companyId, portalId, redirectId),
      patch,
    )
  } catch (e: unknown) {
    const response = (e as { response?: Response }).response
    if (response) {
      const json = await response.json().catch(() => ({}))
      const err = new Error('updatePortalRedirect failed') as Error & { errors?: unknown }
      err.errors = json.errors
      throw err
    }
    throw e
  }
}

export const deletePortalRedirect = (
  companyId: string,
  portalId: string,
  redirectId: string,
): Promise<void> =>
  apiClient.delete<void>(
    CONTENT_SPACE_ROUTES.PRIVATE.PORTAL_REDIRECT(companyId, portalId, redirectId),
  )
